/**
 * Custom hook for managing PDF snippet overlay
 *
 * Extracted from PDFViewerModal.tsx
 */

import { useState, useEffect, useRef } from 'react';
import type { SnippetCoordinates, PDFCoords } from '../types/pdf-viewer.types';
import logger from '../../../utils/simpleLogger';

export interface UsePDFSnippetOverlayProps {
  snippetCoordinates?: SnippetCoordinates;
  pageNumber: number;
  scale: number;
  numPages: number;
  containerRef: React.RefObject<HTMLDivElement>;
  pageRefs: React.MutableRefObject<{ [key: number]: HTMLDivElement | null }>;
}

export interface UsePDFSnippetOverlayReturn {
  snippetOverlayRect: PDFCoords | null;
}

/**
 * Manages snippet overlay positioning and coordinate conversion
 */
export function usePDFSnippetOverlay({
  snippetCoordinates,
  pageNumber,
  scale,
  numPages,
  containerRef,
  pageRefs,
}: UsePDFSnippetOverlayProps): UsePDFSnippetOverlayReturn {
  const [snippetOverlayRect, setSnippetOverlayRect] = useState<PDFCoords | null>(null);

  useEffect(() => {
    if (!snippetCoordinates || !containerRef.current) {
      setSnippetOverlayRect(null);
      return;
    }

    const updateSnippetOverlay = () => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const currentPageEl = pageRefs.current[pageNumber] ?? document.querySelector('.react-pdf__Page') as HTMLElement | null;

      if (!currentPageEl) {
        console.warn('❌ No se encontró el elemento de la página para calcular el overlay del snippet');
        return;
      }

      const pageRect = currentPageEl.getBoundingClientRect();

      logger.debug?.('🎯 Convirtiendo coordenadas snippet:', { snippetCoordinates, pageRect });

      // Verificar si las coordenadas son relativas (porcentuales)
      const isRelative = snippetCoordinates.is_relative !== false;

      let newOverlayRect: PDFCoords;

      if (isRelative) {
        // Convertir porcentajes (origen inferior-izquierdo) a posición absoluta dentro de la página
        const leftOffset = pageRect.left - containerRect.left;
        const topOffset = pageRect.top - containerRect.top;

        const widthPx = (snippetCoordinates.width / 100) * pageRect.width;
        const heightPx = (snippetCoordinates.height / 100) * pageRect.height;

        // Convertir coordenada Y del sistema PDF (origen inferior) al sistema DOM (origen superior)
        const yFromTopPercent = 100 - snippetCoordinates.y - snippetCoordinates.height;

        newOverlayRect = {
          x: leftOffset + (snippetCoordinates.x / 100) * pageRect.width,
          y: topOffset + (yFromTopPercent / 100) * pageRect.height,
          width: widthPx,
          height: heightPx
        };

        logger.info('   ✅ Coordenadas absolutas calculadas (relativas):', newOverlayRect);
      } else {
        // Ya son absolutas respecto al contenedor
        logger.info('   ℹ️ Coordenadas ya son absolutas');
        newOverlayRect = {
          x: snippetCoordinates.x,
          y: snippetCoordinates.y,
          width: snippetCoordinates.width,
          height: snippetCoordinates.height
        };
      }

      // Only update state if the rect actually changed (prevents render loops)
      setSnippetOverlayRect(prev => {
        if (!prev) return newOverlayRect;
        const epsilon = 1; // Allow 1px difference to avoid floating point precision issues
        if (Math.abs(prev.x - newOverlayRect.x) < epsilon &&
            Math.abs(prev.y - newOverlayRect.y) < epsilon &&
            Math.abs(prev.width - newOverlayRect.width) < epsilon &&
            Math.abs(prev.height - newOverlayRect.height) < epsilon) {
          return prev; // Return previous value to avoid unnecessary re-render
        }
        return newOverlayRect;
      });
    };

    // Actualizar inmediatamente
    updateSnippetOverlay();

    // También actualizar cuando cambie el tamaño del contenedor
    // Use a debounced callback to prevent excessive updates
    let resizeTimeout: NodeJS.Timeout | null = null;
    const resizeObserver = new ResizeObserver(() => {
      // Debounce resize observer to prevent render loops
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(() => {
        updateSnippetOverlay();
      }, 16); // ~1 frame delay
    });
    const containerElement = containerRef.current;
    if (containerElement) {
      resizeObserver.observe(containerElement);
    }

    const pageElement = pageRefs.current[pageNumber];
    if (pageElement) {
      resizeObserver.observe(pageElement);
    }

    // Use requestAnimationFrame for initial update after render, but only once
    const rafId = requestAnimationFrame(() => {
      updateSnippetOverlay();
    });

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(rafId);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, [snippetCoordinates, scale, pageNumber, numPages, containerRef, pageRefs]);

  return {
    snippetOverlayRect,
  };
}
